import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useCurrency } from "../lib/currency";
import { Shield, User, LogOut, Wallet, ChevronDown, Check, KeyRound, AlertTriangle } from "lucide-react";
import { Button } from "./ui/button";
import api from "../lib/api";
import NotificationCenter from "./NotificationCenter";

function CurrencyMenu({ testidPrefix = "" }) {
  const { code, rates, setCode } = useCurrency();
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState(null); // {top, left} in viewport coords
  const wrapRef = useRef(null);
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      const inWrap = wrapRef.current && wrapRef.current.contains(e.target);
      const inMenu = menuRef.current && menuRef.current.contains(e.target);
      if (!inWrap && !inMenu) setOpen(false);
    };
    const reposition = () => {
      if (!btnRef.current) return;
      const rect = btnRef.current.getBoundingClientRect();
      const menuWidth = 224; // matches w-56
      const gutter = 8;
      // Prefer aligning the menu's right edge to the button's right edge,
      // then clamp inside viewport so it never leaves the screen.
      let left = rect.right - menuWidth;
      const maxLeft = window.innerWidth - menuWidth - gutter;
      if (left > maxLeft) left = maxLeft;
      if (left < gutter) left = gutter;
      setMenuPos({ top: rect.bottom + 8, left });
    };
    reposition();
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("touchstart", onDoc, { passive: true });
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("touchstart", onDoc);
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
    };
  }, [open]);
  const active = rates?.[code];
  const entries = Object.entries(rates || {});
  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        ref={btnRef}
        data-testid={`${testidPrefix}currency-select`}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-1.5 rounded-full border border-[var(--av-border)] bg-[rgba(11,12,16,0.55)] px-3 py-1.5 text-xs font-mono text-[var(--av-gold)] hover:border-[var(--av-gold)] hover:bg-[rgba(212,175,55,0.08)] transition-colors focus:outline-none focus:ring-1 focus:ring-[var(--av-gold)]"
      >
        <span className="text-sm leading-none">{active?.symbol || "£"}</span>
        <span className="tracking-wider">{code}</span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && menuPos && (
        <ul
          role="listbox"
          ref={menuRef}
          data-testid={`${testidPrefix}currency-menu`}
          style={{ position: "fixed", top: menuPos.top, left: menuPos.left }}
          className="w-56 rounded-xl border border-[var(--av-border)] bg-[rgba(18,20,28,0.98)] backdrop-blur-md shadow-2xl overflow-hidden z-[100] py-1"
        >
          <li className="px-3 py-2 border-b border-[var(--av-border)]/60 text-[10px] text-[var(--av-text-muted)] font-mono leading-snug">
            All prices are charged in GBP.<br/>Other currencies shown are ≈ approximate.
          </li>
          {entries.length === 0 && (
            <li className="px-3 py-3 text-xs text-[var(--av-text-dim)] font-mono">Loading currencies…</li>
          )}
          {entries.map(([c, info]) => (
            <li key={c}>
              <button
                type="button"
                data-testid={`${testidPrefix}currency-opt-${c}`}
                onClick={() => { setCode(c); setOpen(false); }}
                className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 text-xs font-mono hover:bg-[rgba(212,175,55,0.08)] ${c === code ? "text-[var(--av-gold)]" : "text-[var(--av-text)]"}`}
              >
                <span className="flex items-center gap-2">
                  <span className="text-sm w-5 text-center">{info.symbol}</span>
                  <span className="tracking-wider">{c}</span>
                  <span className="text-[var(--av-text-dim)] hidden sm:inline">· {info.name}</span>
                </span>
                {c === code && <Check className="w-3.5 h-3.5 text-[var(--av-gold)]" />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const NAV_LINKS = [
  { to: "/store",     label: "Apex Store",  testid: "nav-store" },
  { to: "/#offers",   label: "Vault Offers", testid: "nav-offers" },
  { to: "/#rewards",  label: "Rewards",      testid: "nav-rewards" },
  { to: "/reviews",   label: "Reviews",      testid: "nav-reviews" },
  { to: "/#faq",      label: "FAQ",          testid: "nav-faq" },
];

export default function Header() {
  const { user, logout } = useAuth();
  const { format } = useCurrency();
  const nav = useNavigate();
  const loc = useLocation();
  const [storeState, setStoreState] = useState(null); // {status, message}
  useEffect(() => {
    let cancelled = false;
    api.get("/store/status").then(r => { if (!cancelled) setStoreState(r.data); }).catch(() => {});
    return () => { cancelled = true; };
  }, [loc.pathname]);
  const isStaff = user && ["owner","admin","fulfilment","support","finance"].includes(user.role);
  const isActive = (to) => {
    const [path, hash = ""] = to.split("#");
    if (hash) return loc.pathname === "/" && loc.hash === "#" + hash;
    return loc.pathname === path;
  };
  const isPaused = storeState && (storeState.status === "maintenance" || storeState.status === "closed");
  const pauseCopy = storeState?.status === "maintenance"
    ? "The Vault is briefly under maintenance — new orders are paused. Please check back shortly."
    : "Sales are paused right now — new orders can't be placed. Follow us for updates on when the Vault reopens.";
  return (
    <>
    <header className="fixed top-0 left-0 right-0 z-40 backdrop-blur-md bg-[rgba(11,12,16,0.92)] border-b border-[var(--av-border)]">
      {/* Row 1: brand + actions */}
      <div className="max-w-7xl mx-auto px-3 sm:px-8 pt-3 pb-2 sm:py-4 flex items-center justify-between gap-2 sm:gap-4">
        <Link to="/" data-testid="brand-link" className="flex items-center gap-2 min-w-0 mr-1">
          <Shield className="w-5 h-5 sm:w-6 sm:h-6 text-[var(--av-gold)] shrink-0" />
          <span className="font-serif text-sm sm:text-xl tracking-[0.08em] sm:tracking-[0.15em] gold-text font-bold whitespace-nowrap">ARTHUR'S VAULT</span>
        </Link>
        {/* Desktop nav — hidden below lg */}
        <nav className="hidden lg:flex items-center gap-6 text-sm text-[var(--av-text-dim)]">
          {NAV_LINKS.map(l => (
            <Link key={l.to} to={l.to} data-testid={l.testid}
              className={`hover:text-[var(--av-gold)] ${isActive(l.to) ? "text-[var(--av-gold)]" : ""}`}>
              {l.label}
            </Link>
          ))}
        </nav>
        {/* Actions — always visible */}
        <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
          {/* Currency menu only on sm+ — on mobile it lives in row 2 */}
          <div className="hidden sm:block"><CurrencyMenu /></div>
          {user && (
            <span className="pill pill-purple hidden sm:inline-flex" data-testid="rewards-balance">
              <Wallet className="w-3 h-3" />{format(user.reward_available || 0)}
            </span>
          )}
          {isStaff && (
            <button
              data-testid="admin-link"
              onClick={() => nav("/admin")}
              className="hidden sm:inline-flex items-center gap-1.5 rounded-full border border-[var(--av-gold)] bg-[rgba(212,175,55,0.08)] px-3 py-1.5 text-xs font-mono tracking-wider text-[var(--av-gold)] hover:bg-[rgba(212,175,55,0.18)] transition-colors"
            >
              <KeyRound className="w-3.5 h-3.5" /> ADMIN
            </button>
          )}
          {user ? (
            <>
              <Button variant="ghost" data-testid="my-vault-btn" onClick={() => nav("/my-vault")}
                className="text-[var(--av-gold)] hover:bg-[rgba(212,175,55,0.1)] px-3 min-h-[44px]">
                <User className="w-4 h-4 sm:mr-1" />
                <span className="hidden sm:inline">My Vault</span>
              </Button>
              <Button variant="ghost" data-testid="logout-btn" onClick={() => { logout(); nav("/"); }}
                className="text-[var(--av-text-dim)] px-3 min-h-[44px]">
                <LogOut className="w-4 h-4" />
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" data-testid="login-link" onClick={() => nav("/auth?mode=login")}
                className="text-[var(--av-text-dim)] hover:text-[var(--av-gold)] px-3 min-h-[44px] text-xs sm:text-sm">
                Login
              </Button>
              <button data-testid="register-link" onClick={() => nav("/auth?mode=register")}
                className="btn-gold text-xs sm:text-sm whitespace-nowrap px-4 min-h-[44px]">
                Create Account
              </button>
            </>
          )}
        </div>
      </div>
      {/* Row 2: mobile-only scrollable nav pills (hidden lg+) */}
      <nav
        data-testid="mobile-nav"
        className="lg:hidden overflow-x-auto no-scrollbar border-t border-[var(--av-border)]/60 bg-[rgba(11,12,16,0.65)]"
      >
        <div className="flex items-center gap-1.5 px-3 sm:px-6 py-2 w-max min-w-full">
          <div className="sm:hidden shrink-0"><CurrencyMenu testidPrefix="m-" /></div>
          {isStaff && (
            <button
              data-testid="m-admin-link"
              onClick={() => nav("/admin")}
              className="shrink-0 inline-flex items-center gap-1.5 rounded-full border border-[var(--av-gold)] bg-[rgba(212,175,55,0.1)] px-3 py-1.5 text-[11px] font-mono tracking-wider text-[var(--av-gold)]"
            >
              <KeyRound className="w-3 h-3" /> ADMIN
            </button>
          )}
          {NAV_LINKS.map(l => (
            <Link key={l.to} to={l.to} data-testid={`m-${l.testid}`}
              className={`shrink-0 rounded-full border px-3 py-1.5 text-[12px] font-medium tracking-wide transition-colors
                ${isActive(l.to)
                  ? "border-[var(--av-gold)] bg-[rgba(212,175,55,0.12)] text-[var(--av-gold)]"
                  : "border-[var(--av-border)] text-[var(--av-text-dim)] hover:text-[var(--av-gold)] hover:border-[var(--av-gold)]"}`}>
              {l.label}
            </Link>
          ))}
        </div>
      </nav>
      {isPaused && (
        <div
          data-testid="store-paused-banner"
          className="border-t border-[var(--av-purple)] bg-[rgba(148,0,211,0.15)] text-[var(--av-purple)]"
        >
          <div className="max-w-7xl mx-auto px-3 sm:px-8 py-2 flex items-center gap-2 text-xs sm:text-sm">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            <span className="font-medium">
              {storeState?.message?.trim() ? storeState.message : pauseCopy}
            </span>
          </div>
        </div>
      )}
    </header>
    <NotificationCenter/>
    </>
  );
}
